<template>
    <div class="bus-management-container container">
      <el-card shadow="hover">
        <template #header>
          <div class="card-header">
            <h2>إدارة حافلات النقل المدرسي</h2>
            <el-button type="primary" @click="handleCreate">
              <el-icon><Plus /></el-icon> إضافة حافلة جديدة
            </el-button>
          </div>
        </template>
  
        <el-alert
          title="ما هي هذه الواجهة؟"
          type="info"
          description="هذه الواجهة هي المركز الرئيسي لإدارة أسطول حافلات المدرسة. يمكنك من هنا إضافة الحافلات، إسناد سائق لكل حافلة، والأهم من ذلك، تحديد الطلاب المسجلين في كل حافلة. هذه الخطوة ضرورية لتفعيل ميزة تتبع الحافلات لأولياء الأمور."
          :closable="false"
          show-icon
          style="margin-bottom: 20px;"
        />
  
        <el-table :data="buses" v-loading="loading" border style="width: 100%">
          <el-table-column prop="bus_number" label="رقم الحافلة" />
          <el-table-column prop="plate_number" label="رقم اللوحة" />
          <el-table-column label="السائق المسند">
            <template #default="{ row }">
              {{ row.driver ? row.driver.name : 'غير مسند' }}
            </template>
          </el-table-column>
          <el-table-column prop="students_count" label="عدد الطلاب" />
          <el-table-column label="الإجراءات" width="280" fixed="right">
            <template #default="{ row }">
              <el-button size="small" @click="handleEdit(row)">تعديل</el-button>
              <el-button size="small" type="success" @click="handleManageStudents(row)">
                <el-icon><User /></el-icon> إدارة الطلاب
              </el-button>
              <el-popconfirm
                title="هل أنت متأكد من حذف هذه الحافلة؟"
                @confirm="handleDelete(row.id)"
              >
                <template #reference><el-button size="small" type="danger">حذف</el-button></template>
              </el-popconfirm>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
  
      <!-- Dialog لإنشاء/تعديل الحافلة -->
      <el-dialog v-model="busDialogVisible" :title="dialogTitle" width="40%">
        <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
          <el-form-item label="رقم الحافلة" prop="bus_number">
            <el-input v-model="form.bus_number" placeholder="مثال: Bus 01" />
          </el-form-item>
          <el-form-item label="رقم اللوحة" prop="plate_number">
            <el-input v-model="form.plate_number" />
          </el-form-item>
          <el-form-item label="السائق" prop="driver_id">
            <el-select v-model="form.driver_id" placeholder="اختر سائقًا" style="width: 100%;" clearable>
              <el-option v-for="driver in drivers" :key="driver.id" :label="driver.name" :value="driver.id" />
            </el-select>
          </el-form-item>
        </el-form>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="busDialogVisible = false">إلغاء</el-button>
            <el-button type="primary" @click="submitForm" :loading="formLoading">حفظ</el-button>
          </span>
        </template>
      </el-dialog>
  
      <!-- Dialog لإسناد الطلاب -->
      <el-dialog v-model="studentDialogVisible" :title="`إدارة طلاب الحافلة: ${selectedBus?.bus_number}`" width="70%">
          <el-transfer
            v-model="assignedStudentIds"
            :data="allStudents"
            :titles="['الطلاب المتاحون', 'الطلاب في هذه الحافلة']"
            :props="{ key: 'id', label: 'name' }"
            filterable
            target-order="push"
            style="width: 100%; display: flex; justify-content: center;"
          />
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="studentDialogVisible = false">إلغاء</el-button>
            <el-button type="primary" @click="saveStudentAssignments" :loading="isSavingStudents">حفظ</el-button>
          </span>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { ElMessage, ElPopconfirm } from 'element-plus';
  import { Plus, User } from '@element-plus/icons-vue';
  
  export default {
    name: 'BusManagement',
    components: { Plus, User, ElPopconfirm },
    data() {
      return {
        api: null,
        loading: true,
        buses: [],
        drivers: [],
        allStudents: [],
        
        busDialogVisible: false,
        formLoading: false,
        form: this.getInitialForm(),
        rules: {
          bus_number: [{ required: true, message: 'رقم الحافلة مطلوب', trigger: 'blur' }],
          plate_number: [{ required: true, message: 'رقم اللوحة مطلوب', trigger: 'blur' }],
        },
        
        studentDialogVisible: false,
        isSavingStudents: false,
        selectedBus: null,
        assignedStudentIds: [],
      };
    },
    computed: {
      dialogTitle() {
        return this.form.id ? 'تعديل بيانات الحافلة' : 'إضافة حافلة جديدة';
      },
    },
    created() {
      this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
      this.api.interceptors.request.use((config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      });
      this.fetchInitialData();
    },
    methods: {
      getInitialForm() {
        return { id: null, bus_number: '', plate_number: '', driver_id: null };
      },
      async fetchInitialData() {
        this.loading = true;
        try {
          const [busesRes, driversRes, studentsRes] = await Promise.all([
            this.api.get('/buses'),
            this.api.get('/drivers'),
            this.api.get('/students', { params: { per_page: 1000 } }) // Fetch all students for the transfer component
          ]);
          this.buses = busesRes.data;
          this.drivers = driversRes.data;
          this.allStudents = studentsRes.data.data || studentsRes.data;
        } catch (error) {
          ElMessage.error('فشل في جلب البيانات الأولية.');
        } finally {
          this.loading = false;
        }
      },
      handleCreate() {
        this.form = this.getInitialForm();
        this.busDialogVisible = true;
      },
      handleEdit(bus) {
        this.form = { ...bus };
        this.busDialogVisible = true;
      },
      async submitForm() {
        if (!this.$refs.formRef) return;
        await this.$refs.formRef.validate(async (valid) => {
          if (!valid) return;
          this.formLoading = true;
          try {
            if (this.form.id) {
              await this.api.put(`/buses/${this.form.id}`, this.form);
              ElMessage.success('تم تحديث الحافلة بنجاح!');
            } else {
              await this.api.post('/buses', this.form);
              ElMessage.success('تم إنشاء الحافلة بنجاح!');
            }
            this.busDialogVisible = false;
            this.fetchInitialData();
          } catch (error) {
            ElMessage.error(error.response?.data?.message || 'فشلت العملية.');
          } finally {
            this.formLoading = false;
          }
        });
      },
      async handleDelete(id) {
        try {
          await this.api.delete(`/buses/${id}`);
          ElMessage.success('تم حذف الحافلة بنجاح!');
          this.fetchInitialData();
        } catch (error) {
          ElMessage.error(error.response?.data?.message || 'فشل في حذف الحافلة.');
        }
      },
      async handleManageStudents(bus) {
          this.selectedBus = bus;
          this.studentDialogVisible = true;
          try {
              const response = await this.api.get(`/buses/${bus.id}`);
              this.assignedStudentIds = response.data.students.map(s => s.id);
          } catch (error) {
              ElMessage.error('فشل في جلب الطلاب المسندين لهذه الحافلة.');
          }
      },
      async saveStudentAssignments() {
          if (!this.selectedBus) return;
          this.isSavingStudents = true;
          try {
              await this.api.post(`/buses/${this.selectedBus.id}/assign-students`, {
                  student_ids: this.assignedStudentIds,
              });
              ElMessage.success('تم تحديث قائمة الطلاب بنجاح!');
              this.studentDialogVisible = false;
              this.fetchInitialData(); // Refresh data to update student count
          } catch (error) {
              ElMessage.error('فشل في حفظ التغييرات.');
          } finally {
              this.isSavingStudents = false;
          }
      }
    },
  };
  </script>
  
  <style scoped>
  .bus-management-container { padding: 20px; }
  .card-header { display: flex; justify-content: space-between; align-items: center; }
  </style>
  